import React, { useCallback, useEffect, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  Activity,
  ArrowDownCircle,
  ArrowUpCircle,
  Coins,
  DollarSign,
  Hash,
  Image,
} from 'lucide-react';
import { TaskyEmptyState, TaskyErrorState, TaskySpinner, TaskyStatCard } from '../../tasky';
import adminUsageService, {
  UserUsageResponse,
  PeriodType,
} from '../../../services/api/adminUsageService';

// recharts へ渡す色のみ hex 定数を許可（tasky-* トークンと同値）
const CHART_COLORS = ['#616FC9', '#44C2A4', '#C96162', '#9675C9', '#C9A061'];

const PERIOD_OPTIONS: { value: PeriodType; label: string }[] = [
  { value: 'day', label: '今日' },
  { value: 'week', label: '過去7日間' },
  { value: 'month', label: '過去30日間' },
  { value: 'year', label: '過去1年間' },
];

type DisplayMode = 'credits' | 'tokens';

export interface UserUsageDashboardProps {
  userId: number;
}

const segmentButtonClass = (active: boolean) =>
  `px-3 py-1.5 rounded-tasky-xs text-sm transition-colors ${
    active ? 'bg-primary text-white font-medium' : 'text-gray-600 hover:bg-warm-hover'
  }`;

const chartCardClass = 'bg-white rounded-tasky-md border border-warm-border p-6';
const chartTitleClass = 'text-sm font-semibold text-dark mb-5';

const UserUsageDashboard = React.memo<UserUsageDashboardProps>(({ userId }) => {
  const [data, setData] = useState<UserUsageResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<PeriodType>('month');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('credits');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminUsageService.getUserUsage(userId, period);
      if (response.success) {
        setData(response.data);
      } else {
        setError('データの取得に失敗しました');
      }
    } catch (err) {
      console.error('Failed to fetch user usage:', err);
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [userId, period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 py-12 text-sm text-gray-500">
        <TaskySpinner size="md" />
        読み込み中...
      </div>
    );
  }

  if (error) {
    return <TaskyErrorState description={error} />;
  }

  if (!data) {
    return <TaskyEmptyState title="データがありません" />;
  }

  return (
    <div>
      {/* 期間切替・表示モード切替 */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="inline-flex items-center gap-1 bg-white rounded-tasky-sm border border-warm-border p-1">
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setPeriod(option.value)}
              className={segmentButtonClass(period === option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-1 bg-white rounded-tasky-sm border border-warm-border p-1">
          <button
            type="button"
            onClick={() => setDisplayMode('credits')}
            className={segmentButtonClass(displayMode === 'credits')}
          >
            クレジット
          </button>
          <button
            type="button"
            onClick={() => setDisplayMode('tokens')}
            className={segmentButtonClass(displayMode === 'tokens')}
          >
            トークン
          </button>
        </div>
      </div>

      {/* サマリカード */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <TaskyStatCard
          label="総リクエスト数"
          value={formatNumber(data.summary.total_requests)}
          icon={<Activity />}
          iconTone="primary"
        />
        {displayMode === 'credits' ? (
          <>
            <TaskyStatCard
              label="Taskyクレジット"
              value={formatNumber(Math.round(data.summary.total_credits || 0))}
              icon={<Coins />}
              iconTone="success"
            />
            <TaskyStatCard
              label="画像生成数"
              value={formatNumber(data.summary.total_image_count)}
              icon={<Image />}
              iconTone="warning"
            />
            <TaskyStatCard
              label="推定コスト"
              value={`$${data.summary.total_cost_usd.toFixed(2)}`}
              icon={<DollarSign />}
              iconTone="destructive"
            />
          </>
        ) : (
          <>
            <TaskyStatCard
              label="総トークン数"
              value={formatNumber(data.summary.total_tokens)}
              icon={<Hash />}
              iconTone="purple"
            />
            <TaskyStatCard
              label="入力トークン"
              value={formatNumber(data.summary.total_input_tokens)}
              icon={<ArrowDownCircle />}
              iconTone="purple"
            />
            <TaskyStatCard
              label="出力トークン"
              value={formatNumber(data.summary.total_output_tokens)}
              icon={<ArrowUpCircle />}
              iconTone="purple"
            />
          </>
        )}
      </div>

      {/* チャート */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={`${chartCardClass} lg:col-span-2`}>
          <h3 className={chartTitleClass}>{displayMode === 'credits' ? '日別クレジット使用量' : '日別トークン使用量'}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.daily_trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                tickFormatter={formatNumber}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                labelFormatter={(label) => new Date(label).toLocaleDateString('ja-JP')}
                formatter={(value: number | undefined): [string, 'トークン' | 'クレジット'] => [
                  formatNumber(displayMode === 'credits' ? Math.round(value ?? 0) : (value ?? 0)),
                  displayMode === 'credits' ? 'クレジット' : 'トークン',
                ]}
              />
              <Area
                type="monotone"
                dataKey={displayMode === 'credits' ? 'total_credits' : 'total_tokens'}
                stroke={displayMode === 'credits' ? CHART_COLORS[1] : CHART_COLORS[0]}
                fill={displayMode === 'credits' ? CHART_COLORS[1] : CHART_COLORS[0]}
                fillOpacity={0.3}
                name={displayMode === 'credits' ? 'クレジット' : 'トークン数'}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className={`${chartCardClass} lg:col-span-2`}>
          <h3 className={chartTitleClass}>{displayMode === 'credits' ? '機能別クレジット' : '機能別使用量'}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.by_feature as unknown as Record<string, unknown>[]}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey={displayMode === 'credits' ? 'total_credits' : 'total_tokens'}
                nameKey="feature_label"
                label={({ name, percent }: { name?: string; percent?: number }) =>
                  `${name || ''} (${((percent || 0) * 100).toFixed(0)}%)`
                }
                labelLine={false}
              >
                {data.by_feature.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number | undefined): [string, 'トークン' | 'クレジット'] => [
                  formatNumber(displayMode === 'credits' ? Math.round(value ?? 0) : (value ?? 0)),
                  displayMode === 'credits' ? 'クレジット' : 'トークン',
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
});
UserUsageDashboard.displayName = 'UserUsageDashboard';

export default UserUsageDashboard;
